import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";

const inputCls = "w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-[#EC5B24] bg-white text-slate-900 font-normal";
const AIRLINES = [["6E", "IndiGo"], ["AI", "Air India"], ["IX", "Air India Express"], ["QP", "Akasa Air"], ["SG", "SpiceJet"], ["EK", "Emirates"], ["QR", "Qatar Airways"], ["SQ", "Singapore Airlines"], ["TG", "Thai Airways"], ["UK", "Vistara"], ["KB", "Druk Air"], ["EY", "Etihad"], ["FZ", "flydubai"], ["G9", "Air Arabia"]];
const signed = (v) => v.replace(/[^\d-]/g, "").replace(/(?!^)-/g, "");

export const PriceRules = () => {
  const [s, setS] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get("/admin/settings").then((r) => { setS(r.data); setRows(Object.entries(r.data.airline_markups || {}).map(([code, v]) => ({ code, v: String(v) }))); }).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-[#EC5B24]" />;
  const save = async () => {
    setBusy(true);
    const airline_markups = Object.fromEntries(rows.filter((r) => r.code && +r.v).map((r) => [r.code, +r.v]));
    try { await http.put("/admin/settings", { ...s, markup_domestic: +s.markup_domestic || 0, markup_intl: +s.markup_intl || 0, airline_markups }); toast.success("Flight prices saved — new searches use them"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setRow = (i, p) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <div className="space-y-4 max-w-2xl" data-testid="admin-price-rules">
      <p className="text-sm text-slate-500">Add (+) or take off (−) a fixed amount per traveller on every live fare. Use a minus sign to lower prices. This is on top of the ₹{s.instant_discount} instant discount. To change one customer's price, open their unpaid booking.</p>
      <div className="bg-white rounded-2xl border p-4 grid sm:grid-cols-2 gap-3">
        {[["markup_domestic", "Domestic flights (₹ per traveller)"], ["markup_intl", "International flights (₹ per traveller)"]].map(([k, l]) => (
          <label key={k} className="text-xs font-semibold text-slate-500">{l}
            <input data-testid={`price-${k}`} value={s[k] ?? 0} onChange={(e) => setS({ ...s, [k]: signed(e.target.value) })} placeholder="e.g. 500 or -1000" className={`${inputCls} mt-1 font-mono`} />
          </label>
        ))}
      </div>
      <div className="bg-white rounded-2xl border p-4 space-y-2">
        <div className="font-semibold text-sm">Extra for each airline (₹ per traveller)</div>
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2" data-testid={`airline-markup-${i}`}>
            <select data-testid={`airline-markup-code-${i}`} value={r.code} onChange={(e) => setRow(i, { code: e.target.value })} className={inputCls}>
              <option value="">Choose airline</option>{AIRLINES.map(([c, n]) => <option key={c} value={c}>{n} ({c})</option>)}
              {r.code && !AIRLINES.some(([c]) => c === r.code) && <option value={r.code}>{r.code}</option>}
            </select>
            <input data-testid={`airline-markup-value-${i}`} value={r.v} onChange={(e) => setRow(i, { v: signed(e.target.value) })} placeholder="+300 or -500" className={`${inputCls} font-mono w-36`} />
            <button data-testid={`airline-markup-remove-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="h-9 w-9 shrink-0 grid place-items-center rounded-lg text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button data-testid="airline-markup-add" onClick={() => setRows([...rows, { code: "", v: "" }])} className="rounded-full border px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><Plus className="h-3 w-3" /> Add airline</button>
      </div>
      <button data-testid="price-rules-save" disabled={busy} onClick={save} className="rounded-full bg-[#1C1A4A] text-white font-semibold px-6 py-2.5 flex items-center gap-1">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save flight prices</button>
    </div>
  );
};
